/**
 * Simplified API Helper (Legacy migration support removed)
 * 
 * This utility provides simplified API access since all services have been migrated
 * to the modern architecture. Legacy support has been removed.
 */

import * as modernActions from '../Action/modernAdminActions';

/**
 * React Hook for API access
 * Simplified version that always uses modern APIs
 */
export const useMigrationHelper = () => {
    // Always use modern API since migration is complete
    const useModernAPI = true;
    
    // Simplified response handling (backend now returns consistent format)
    const transformResponse = (response) => {
        // Backend now returns data in expected format directly
        return response;
    };
    
    return {
        useModernAPI,
        // All modules now use modern services
        isModernEnabled: () => true,
        transformResponse,
        // Migration is complete
        getMigrationStatus: () => ({ completed: true, modern: 100 })
    };
};



/**
 * Create a migrated action that uses modern implementation
 * @param {string} actionName - Name of the action
 * @returns {Function} - The appropriate action function
 */
export const createMigratedAction = (actionName) => {
    // All actions now use modern implementation
    const modernAction = modernActions[actionName];
    
    if (!modernAction) {
        console.warn(`⚠️ No modern action found for: ${actionName}`);
        return () => Promise.reject(new Error(`Action ${actionName} not found`));
    }

    return async (...args) => {
        try {
            return await modernAction(...args);
        } catch (error) {
            console.error(`❌ Error in modern action ${actionName}:`, error);
            throw error;
        }
    };
};

/**
 * Create all migrated actions
 * @returns {Object} - Object containing all migrated actions
 */
export const createMigratedActions = () => {
    const migratedActions = {};
    
    // Get all available modern actions
    Object.keys(modernActions).forEach(actionName => {
        migratedActions[actionName] = createMigratedAction(actionName);
    });
    
    return migratedActions;
};

/**
 * Get migration status for all modules
 * @returns {Object} - Migration status object
 */
export const getMigrationStatus = () => {
    return {
        userService: true,
        rbacService: true,
        patientService: true,
        formsService: true,
        masterDataService: true,
        migrationLogging: false,
        fallbackEnabled: false,
        completed: true
    };
};

/**
 * Enable modern service for a specific module
 * @param {string} module - Module name
 */
export const enableModernService = (module) => {
    console.log(`✅ Modern service already enabled for ${module} (migration complete)`);
};

/**
 * Disable modern service for a specific module (fallback to legacy)
 * @param {string} module - Module name
 */
export const disableModernService = (module) => {
    console.warn(`⚠️ Cannot disable modern service for ${module} (migration complete)`);
};

/**
 * Performance monitoring for migration
 */
export const createPerformanceMonitor = () => {
    const metrics = {
        modernCalls: 0,
        legacyCalls: 0,
        errors: 0,
        averageResponseTime: 0,
        responseTimes: []
    };

    return {
        recordCall: (type, responseTime) => {
            if (type === 'modern') {
                metrics.modernCalls++;
            } else {
                metrics.legacyCalls++;
            }
            
            metrics.responseTimes.push(responseTime);
            metrics.averageResponseTime = metrics.responseTimes.reduce((a, b) => a + b, 0) / metrics.responseTimes.length;
        },
        
        recordError: () => {
            metrics.errors++;
        },
        
        getMetrics: () => ({ ...metrics }),
        
        reset: () => {
            Object.keys(metrics).forEach(key => {
                if (Array.isArray(metrics[key])) {
                    metrics[key] = [];
                } else {
                    metrics[key] = 0;
                }
            });
        }
    };
};

// Export default migrated actions
export default createMigratedActions(); 